import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

const SLIDE_VISIBLITY_THRESHOLD = 0.7;

export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'initial-slide' || oldValue === newValue) return;

    queueMicrotask(() => {
      if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
      const index = parseInt(newValue, 10) || 0;
      const slideId = this.refs.slides[index]?.getAttribute('slide-id');
      if (slideId) this.select({ id: slideId }, undefined, { animate: false });
    });
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    const count = this.slides?.length || 0;
    count <= 1 ? this.#setupWithoutControls() : this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#destroyScroll();
    this.#removeGlobalListeners();
    this.#resizeObserver?.disconnect();
  }

  /* ------------------------------------------------------------------ */
  /* Core API                                                            */
  /* ------------------------------------------------------------------ */

  async select(input, event, options = {}) {
    if (this.#disabled || !this.#scroll || !this.slides?.length) return;

    const currentSlide = this.slides[this.current];

    this.slides.forEach((s) => {
      if (s.hasAttribute('reveal')) {
        s.removeAttribute('reveal');
        s.setAttribute('aria-hidden', 'true');
      }
    });

    let requestedIndex =
      typeof input === 'number'
        ? input
        : typeof input === 'string'
        ? parseInt(input, 10)
        : this.slides.findIndex((s) => s.getAttribute('slide-id') == input.id);

    if (requestedIndex < 0 || isNaN(requestedIndex)) return;

    if (!this.infinite)
      requestedIndex = clamp(requestedIndex, 0, this.slides.length - 1);

    if (this.slides[requestedIndex] === currentSlide) return;

    event?.preventDefault();

    const instant = prefersReducedMotion() || options.animate === false;
    const lastIndex = this.slides.length - 1;

    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    if (requestedIndex > lastIndex) index = 0;

    const slide = this.slides[index];
    if (!slide) return;

    slide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(slide, { instant });

    const previousIndex = this.current;
    this.current = index;

    this.#centerThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: !!event,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  next(e, o) {
    e?.preventDefault();
    this.select(this.nextIndex, e, o);
  }

  previous(e, o) {
    e?.preventDefault();
    this.select(this.previousIndex, e, o);
  }

  /* ------------------------------------------------------------------ */
  /* Autoplay                                                            */
  /* ------------------------------------------------------------------ */

  play(interval = this.autoplayInterval) {
    if (!interval || this.#interval) return;

    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.suspend();
    this.play();
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  #current = 0;
  #scroll;
  #interval;
  #resizeObserver;
  #disabled = false;
  #dragging = false;

  get current() {
    return this.#current;
  }

  set current(value) {
    this.#current = value;

    const { thumbnails, dots, previous, next, slides, current } = this.refs;
    if (current) current.textContent = `${value + 1}`;

    thumbnails?.forEach((el, i) =>
      el.setAttribute('aria-selected', `${i === value}`)
    );
    dots?.forEach((el, i) =>
      el.setAttribute('aria-selected', `${i === value}`)
    );

    if (previous) previous.disabled = !this.infinite && value === 0;
    if (next) next.disabled = !this.infinite && value === slides?.length - 1;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const val = parseInt(this.getAttribute('autoplay'), 10);
    return Number.isNaN(val) ? undefined : val * 1000;
  }

  get infinite() {
    return this.hasAttribute('infinite');
  }

  get visibleSlides() {
    return getVisibleElements(
      this.refs.scroller,
      this.slides,
      SLIDE_VISIBLITY_THRESHOLD,
      'x'
    );
  }

  get nextIndex() {
    return this.current + Math.max(this.visibleSlides.length, 1);
  }

  get previousIndex() {
    return this.current - Math.max(this.visibleSlides.length, 1);
  }

  get slides() {
    return this.refs.slides?.filter(
      (s) => !s.hasAttribute('hidden') || s.hasAttribute('reveal')
    );
  }

  /* ------------------------------------------------------------------ */
  /* Setup                                                               */
  /* ------------------------------------------------------------------ */

  #setupWithoutControls() {
    this.current = 0;
    this.slides?.[0]?.setAttribute('aria-hidden', 'false');
  }

  #setup() {
    const { scroller } = this.refs;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: () => this.setAttribute('transitioning', ''),
      onScrollEnd: () => {
        this.#updateVisibleSlides();
        this.removeAttribute('transitioning');
      },
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibility);

    this.disabled = this.isNested || this.disabled;
    this.current = this.initialSlideIndex;
    this.resume();

    scheduler.schedule(() => this.#updateVisibleSlides());

    this.#resizeObserver = new ResizeObserver(() =>
      scheduler.schedule(() => this.#updateVisibleSlides())
    );
    this.#resizeObserver.observe(this.refs.slideshowContainer);
  }

  #destroyScroll() {
    if (!this.#scroll) return;
    this.refs.scroller.removeEventListener('mousedown', this.#handleMouseDown);
    this.#scroll.destroy();
    this.#scroll = null;
  }

  #removeGlobalListeners() {
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /* ------------------------------------------------------------------ */
  /* Scroll sync                                                         */
  /* ------------------------------------------------------------------ */

  #handleScroll = () => {
    const index = this.#sync();
    if (index === this.current) return;

    const slide = this.slides[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex: this.current,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );

    this.current = index;
  };

  #handleMouseDown = () => {
    this.suspend();
  };

  #sync() {
    if (!this.slides?.length || !this.#scroll) return this.current;

    const visible = this.visibleSlides;
    if (!visible.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;

    const centers = visible.map((s) => center(s, axis));
    const ref =
      visible.length > 1
        ? scroller.getBoundingClientRect()[axis]
        : center(scroller, axis);

    return this.slides.indexOf(visible[centers.indexOf(closest(centers, ref))]);
  }

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  #centerThumbnail(index, behavior = 'smooth') {
    const thumb = this.refs.thumbnails?.[index];
    const container = this.refs.thumbnailsContainer;
    if (!thumb || !container) return;

    scrollIntoView(thumb, {
      ancestor: container,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const visible = this.visibleSlides;
    this.slides?.forEach((slide) =>
      slide.setAttribute('aria-hidden', `${!visible.includes(slide)}`)
    );
  }

  #handleVisibility = () =>
    document.hidden ? this.pause() : this.resume();

  get initialSlideIndex() {
    return parseInt(this.getAttribute('initial-slide'), 10) || 0;
  }

  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}